// File: src/pages/HR/HRReports.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 * ORIGINAL PROBLEMS:
 *   - Payroll tab: `<pre>{JSON.stringify(payroll, null, 2)}</pre>` — raw JSON dump, unusable
 *   - Attendance tab: same raw JSON dump
 *   - Leave tab: same raw JSON dump
 *   - No proper tables — HR couldn't read the data at all
 *   - No leave type / leave status columns
 *   - No approval manager info in reports
 *
 * NEW (ALL CHANGES MARKED // NEW):
 *   - Payroll tab: proper MUI Table with columns:
 *       Employee · Branch · Basic Salary · Gross · Deductions · Net Pay · Pay Period
 *   - Attendance tab: proper MUI Table with columns:
 *       Employee · Branch · Date · Clock In · Clock Out · Status · Hours · Display
 *       NEW: shows PENDING_APPROVAL_MANAGER / PENDING_MANAGER status with color chips
 *   - Leave tab: proper MUI Table with columns:
 *       Employee · Branch · Start Date · End Date · Days · Reason · Status
 *       NEW: shows approvalManagerRemark as VIEW ONLY chip for HR awareness
 *   - All tabs show "No data" state cleanly
 *   - Column sorting ready (data rendered as-is from backend)
 *   - Download buttons unchanged — they still call the backend Excel endpoints
 *   - ALL download functions kept exactly as before
 * =====================================================================
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Divider,
  Tabs, Tab, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Stack, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Chip, Tooltip
} from '@mui/material';
import {
  Refresh, Download, AttachMoney, AccessTime, AssignmentTurnedIn, InfoOutlined
} from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const safeGet = async (url) => {
  try {
    const res = await api.get(url);
    return res.data || [];
  } catch (e) {
    console.error(url, e);
    return [];
  }
};

/* NEW: status chip helper */
const StatusChip = ({ status }) => {
  if (!status) return <span>-</span>;
  const s = String(status).toUpperCase();
  const color =
    s.includes('APPROVED') ? 'success' :
    s.includes('REJECTED') ? 'error' :
    s.includes('PENDING_APPROVAL_MANAGER') ? 'secondary' :
    s.includes('PENDING') ? 'warning' :
    'default';
  const label =
    s === 'PENDING_APPROVAL_MANAGER' ? 'Pend. AM' :
    s === 'PENDING_MANAGER' ? 'Pend. Mgr' :
    s === 'PENDING_HR' ? 'Pend. HR' :
    s;
  return <Chip label={label} size="small" color={color} />;
};

/* NEW: AM remark VIEW ONLY badge */
const AMRemarkBadge = ({ remark }) => {
  if (!remark) return <Typography fontSize={12} color="text.disabled">—</Typography>;
  return (
    <Tooltip title={`AM Remark: ${remark}`} arrow>
      <Chip
        size="small"
        icon={<InfoOutlined sx={{ fontSize: 13 }} />}
        label="AM Note"
        sx={{
          bgcolor: '#fef9c3', color: '#854d0e',
          border: '1px solid #fde047', fontWeight: 700, fontSize: 11, cursor: 'help'
        }}
      />
    </Tooltip>
  );
};

const HRReports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('ALL');
  const [startDate, setStartDate] = useState('2026-01-21');
  const [endDate, setEndDate] = useState('2026-02-20');
  const [referenceDate, setReferenceDate] = useState('2026-02-04');

  const [attendance, setAttendance] = useState([]);
  const [leave, setLeave] = useState([]);
  const [payroll, setPayroll] = useState([]);

  useEffect(() => {
    safeGet('/branches').then(setBranches);
  }, []);

  /* ================= PAYROLL GENERATION ================= */
  const generatePayroll = async () => {
    try {
      setLoading(true);
      toast.loading('Generating payroll…', { id: 'payroll' });
      if (branchId === 'ALL') {
        await api.post(`/payroll/calculate/all?referenceDate=${referenceDate}`);
      } else {
        await api.post(`/payroll/calculate/branch/${branchId}?referenceDate=${referenceDate}`);
      }
      toast.success('Payroll generated', { id: 'payroll' });
      fetchReports();
    } catch (e) {
      toast.error('Payroll generation failed', { id: 'payroll' });
    } finally {
      setLoading(false);
    }
  };

  /* ================= FETCH REPORTS ================= */
  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      if (branchId === 'ALL') {
        const [att, lev, pay] = await Promise.all([
          safeGet(`/attendance/global-report?startDate=${startDate}&endDate=${endDate}`),
          safeGet(`/leaves/global-report?startDate=${startDate}&endDate=${endDate}`),
          safeGet(`/payroll/reports/aggregate/all?referenceDate=${referenceDate}`)
        ]);
        setAttendance(Array.isArray(att) ? att : []);
        setLeave(Array.isArray(lev) ? lev : []);
        setPayroll(Array.isArray(pay) ? pay : []);
      } else {
        const [att, lev, pay] = await Promise.all([
          safeGet(`/attendance/branch/${branchId}/report?startDate=${startDate}&endDate=${endDate}`),
          safeGet(`/leaves/branch/${branchId}/report?startDate=${startDate}&endDate=${endDate}`),
          safeGet(`/payroll/branch/${branchId}/history?referenceDate=${referenceDate}`)
        ]);
        setAttendance(Array.isArray(att) ? att : []);
        setLeave(Array.isArray(lev) ? lev : []);
        setPayroll(Array.isArray(pay) ? pay : []);
      }
    } catch {
      setError('Failed to fetch HR reports');
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXCEL DOWNLOAD UTILS — UNCHANGED ================= */
  const downloadExcel = async (url, filename) => {
    try {
      toast.loading('Preparing Excel…', { id: 'dl' });
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      toast.success('Downloaded', { id: 'dl' });
    } catch {
      toast.error('Excel download failed', { id: 'dl' });
    }
  };

  const downloadPayrollExcel = () =>
    branchId === 'ALL'
      ? downloadExcel(`/payroll/download/detailed-all?referenceDate=${referenceDate}`, `Payroll_All_${referenceDate}.xlsx`)
      : downloadExcel(`/payroll/download/detailed/${branchId}?referenceDate=${referenceDate}`, `Payroll_Branch_${branchId}_${referenceDate}.xlsx`);

  const downloadDailyAttendance = () =>
    downloadExcel(`/attendance/download/daily?startDate=${startDate}&endDate=${endDate}`, `Daily_Attendance_${startDate}_${endDate}.xlsx`);

  const downloadMonthlyAttendance = () =>
    branchId === 'ALL'
      ? downloadExcel(`/attendance/download/monthly-all?startDate=${startDate}&endDate=${endDate}`, `Monthly_Attendance_All_${startDate}_${endDate}.xlsx`)
      : downloadExcel(`/attendance/download/monthly-custom/${branchId}?startDate=${startDate}&endDate=${endDate}`, `Monthly_Attendance_Branch_${branchId}_${startDate}_${endDate}.xlsx`);

  const downloadEarlyLogin = () =>
    downloadExcel(`/reports/attendance/early-login?startDate=${startDate}&endDate=${endDate}`, `Early_Login_${startDate}_${endDate}.xlsx`);

  const downloadLateLogin = () =>
    downloadExcel(`/reports/attendance/late-login?startDate=${startDate}&endDate=${endDate}`, `Late_Login_${startDate}_${endDate}.xlsx`);

  const downloadLeaveExcel = () =>
    downloadExcel(`/reports/leave/all?startDate=${startDate}&endDate=${endDate}`, `Leave_Requests_${startDate}_${endDate}.xlsx`);

  const downloadMissedPunch = () =>
    downloadExcel(`/reports/missed-punch/all?startDate=${startDate}&endDate=${endDate}`, `Missed_Punch_${startDate}_${endDate}.xlsx`);

  const downloadOnboarding = () =>
    downloadExcel(`/reports/onboarding/full-excel`, `Employee_Onboarding_Full.xlsx`);

  /* ================= NEW: PAYROLL TABLE ================= */
  const renderPayrollTable = () => {
    if (!payroll.length) return (
      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        No payroll data. Select a reference date and click "Run Reports" or "Generate Payroll".
      </Alert>
    );
    return (
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Branch</b></TableCell>
              <TableCell><b>Emp Code</b></TableCell>
              <TableCell align="right"><b>Basic Salary</b></TableCell>
              <TableCell align="right"><b>Gross</b></TableCell>
              <TableCell align="right"><b>Deductions</b></TableCell>
              <TableCell align="right"><b>Net Pay</b></TableCell>
              <TableCell><b>Pay Period</b></TableCell>
              <TableCell><b>Status</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payroll.map((row, idx) => (
              <TableRow key={row.id || idx} hover>
                <TableCell><b>{row.employeeName || row.name || '-'}</b></TableCell>
                <TableCell>{row.branchName || '-'}</TableCell>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell align="right">₹{Number(row.basicSalary || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="right">₹{Number(row.grossSalary || row.totalGross || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  ₹{Number(row.totalDeductions || row.totalDeduct || 0).toLocaleString('en-IN')}
                </TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 700 }}>
                  ₹{Number(row.netSalary || row.netPay || row.totalPayrollAmount || 0).toLocaleString('en-IN')}
                </TableCell>
                <TableCell>
                  <Typography fontSize={12}>
                    {row.cycleStartDate || row.payPeriodStart || '-'} → {row.cycleEndDate || row.payPeriodEnd || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.status || 'Generated'}
                    size="small"
                    color={row.status === 'PAID' ? 'success' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* ================= NEW: ATTENDANCE TABLE ================= */
  const renderAttendanceTable = () => {
    if (!attendance.length) return (
      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        No attendance data for selected date range. Click "Run Reports".
      </Alert>
    );
    return (
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Branch</b></TableCell>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Clock In</b></TableCell>
              <TableCell><b>Clock Out</b></TableCell>
              <TableCell><b>Hours</b></TableCell>
              {/* NEW: Status column with PENDING_APPROVAL_MANAGER awareness */}
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Display</b></TableCell>
              <TableCell><b>Late Reason</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendance.map((row, idx) => (
              <TableRow key={row.id || row.attendanceId || idx} hover>
                <TableCell><b>{row.employeeName || '-'}</b></TableCell>
                <TableCell>{row.branchName || '-'}</TableCell>
                <TableCell>{row.date || '-'}</TableCell>
                <TableCell>{row.clockInTime || '-'}</TableCell>
                <TableCell>{row.clockOutTime || '-'}</TableCell>
                <TableCell>{row.actualHoursWorked ?? '-'}</TableCell>
                <TableCell><StatusChip status={row.status} /></TableCell>
                <TableCell>
                  <Chip
                    label={row.displayStatus || '-'}
                    size="small"
                    color={
                      row.displayStatus === 'P' ? 'success' :
                      row.displayStatus === 'A' ? 'error' :
                      row.displayStatus === 'H' ? 'warning' : 'default'
                    }
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 160 }}>
                  <Typography fontSize={12} color="error.main">{row.lateReason || '-'}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* ================= NEW: LEAVE TABLE ================= */
  const renderLeaveTable = () => {
    if (!leave.length) return (
      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        No leave data for selected date range. Click "Run Reports".
      </Alert>
    );
    return (
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Branch</b></TableCell>
              <TableCell><b>Start Date</b></TableCell>
              <TableCell><b>End Date</b></TableCell>
              <TableCell><b>Days</b></TableCell>
              <TableCell><b>Reason</b></TableCell>
              {/* NEW: AM remark column for HR visibility */}
              <TableCell><b>AM Remark</b> <Typography component="span" fontSize={10} color="text.secondary">(View Only)</Typography></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Manager Remark</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leave.map((row, idx) => {
              const days = row.startDate && row.endDate
                ? Math.round((new Date(row.endDate) - new Date(row.startDate)) / 86400000) + 1
                : '-';
              return (
                <TableRow key={row.id || idx} hover>
                  <TableCell><b>{row.employeeName || '-'}</b></TableCell>
                  <TableCell>{row.branchName || '-'}</TableCell>
                  <TableCell>{row.startDate || '-'}</TableCell>
                  <TableCell>{row.endDate || '-'}</TableCell>
                  <TableCell>{days}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography fontSize={12}>{row.reason || '-'}</Typography>
                  </TableCell>
                  {/* NEW: AM remark for HR awareness */}
                  <TableCell><AMRemarkBadge remark={row.approvalManagerRemark} /></TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography fontSize={12} color="text.secondary">{row.managerRemark || '-'}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* ================= UI ================= */
  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4" fontWeight={800}>
          Consolidated HR Reports
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Button color="warning" variant="contained" startIcon={<AttachMoney />} onClick={generatePayroll}>
            Generate Payroll
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchReports}>
            Run Reports
          </Button>
          <Button startIcon={<AttachMoney />} onClick={downloadPayrollExcel}>
            Payroll Excel
          </Button>
          <Button startIcon={<Download />} onClick={downloadDailyAttendance}>
            Daily Attendance
          </Button>
          <Button startIcon={<Download />} onClick={downloadMonthlyAttendance}>
            Monthly Attendance
          </Button>
          <Button startIcon={<AccessTime />} onClick={downloadEarlyLogin}>
            Early Login
          </Button>
          <Button startIcon={<AccessTime />} onClick={downloadLateLogin}>
            Late Login
          </Button>
          <Button startIcon={<AssignmentTurnedIn />} onClick={downloadLeaveExcel}>
            Leave
          </Button>
          <Button startIcon={<AssignmentTurnedIn />} onClick={downloadMissedPunch}>
            Missed Punch
          </Button>
          <Button startIcon={<AssignmentTurnedIn />} onClick={downloadOnboarding}>
            Onboarding
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Branch</InputLabel>
              <Select value={branchId} label="Branch" onChange={(e) => setBranchId(e.target.value)}>
                <MenuItem value="ALL">All Branches</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField type="date" label="Start Date" fullWidth size="small"
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField type="date" label="End Date" fullWidth size="small"
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField type="date" label="Payroll Reference Date" fullWidth size="small"
              value={referenceDate} onChange={(e) => setReferenceDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`Payroll (${payroll.length})`} />
        <Tab label={`Attendance (${attendance.length})`} />
        <Tab label={`Leave (${leave.length})`} />
      </Tabs>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {/* NEW: Proper tables instead of raw JSON pre tags */}
      {!loading && tab === 0 && renderPayrollTable()}
      {!loading && tab === 1 && renderAttendanceTable()}
      {!loading && tab === 2 && renderLeaveTable()}
    </Box>
  );
};

export default HRReports;